// src/components/Besoin.js
import React, { useEffect, useState, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import { PencilSquare, Search, Funnel, XCircle, SortAlphaDown, SortAlphaUp, CaretUpFill, CaretDownFill } from "react-bootstrap-icons";
import QuickBesoinModal from "./QuickBesoinModal";
import BesoinCard from "./BesoinCard";
import { useBesoins } from "../services/BesoinsContext";
import { fetchWithAuth } from "../services/AuthService";
import { useTranslation } from 'react-i18next';
import { STATUS_IDS, getStateKey } from "../constants/besoinConstants";
import { useAuth } from "../services/AuthContext";
import "../css/Besoin.css";

// Nombre de jours par période, utilisé pour normaliser la fréquence lors du tri
const PERIOD_DAYS = { 1: 1, 2: 7, 3: 30, 4: 365 };

const COLUMNS = [
  { key: "states", label: "besoin.columns.states" },
  { key: "keywords", label: "besoin.columns.keywords" },
  { key: "pride", label: "besoin.columns.pride" },
  { key: "frequency", label: "besoin.columns.frequency" },
  { key: "status", label: "besoin.columns.status" },
];

const Besoin = () => {
  const { t } = useTranslation();
  const { besoins, addBesoin, updateBesoin, deleteBesoin, loading, checkAndLoadBesoins, refreshBesoins } = useBesoins();
  const { userGender } = useAuth();
  const [isQuickBesoinOpen, setIsQuickBesoinOpen] = useState(false);
  const [besoinToEdit, setBesoinToEdit] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activePanel, setActivePanel] = useState(null);
  const [activeStatusFilters, setActiveStatusFilters] = useState([]);
  const [reverseOrder, setReverseOrder] = useState(false);
  const [sortColumn, setSortColumn] = useState(null); // null = tri chronologique par défaut
  const [sortDirection, setSortDirection] = useState("asc");
  const searchInputRef = React.useRef(null);
  const containerRef = React.useRef(null);

  useEffect(() => {
    let mounted = true;
    if (mounted) checkAndLoadBesoins();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activePanel === "search") {
      searchInputRef.current?.focus();
    }
  }, [activePanel]);

  // useEffect(() => {
  //   if (activePanel) {
  //     const container = document.querySelector('.besoin-container');
  //     if (container) container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //   }
  // }, [activePanel]);

  // --- Tri chronologique ---
  const sortedBesoins = useMemo(() => {
    let sorted = [...besoins].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (reverseOrder) sorted = sorted.reverse();
    return sorted;
  }, [besoins, reverseOrder]);

  // --- Filtrage combiné (recherche texte/mots-clés + statut) ---
  const filteredBesoins = useMemo(() => {
    let filtered = sortedBesoins;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((b) =>
        (b.text && b.text.toLowerCase().includes(query)) ||
        (b.keywords && b.keywords.some((kw) => kw.toLowerCase().includes(query)))
      );
    }

    if (activeStatusFilters.length > 0) {
      filtered = filtered.filter((b) => activeStatusFilters.includes(b.status));
    }

    return filtered;
  }, [sortedBesoins, searchQuery, activeStatusFilters]);

  const besoinsCountByStatus = useMemo(() => {
    const counts = {};
    filteredBesoins.forEach((b) => {
      counts[b.status] = (counts[b.status] || 0) + 1;
    });
    return counts;
  }, [filteredBesoins]);

  // --- Valeur comparable pour chaque colonne triable ---
  const getSortValue = (besoin, column) => {
    switch (column) {
      case "states":
        return besoin.states && besoin.states.length > 0
          ? t(getStateKey(Math.min(...besoin.states), userGender)).toLowerCase()
          : "";
      case "keywords":
        return besoin.keywords && besoin.keywords.length > 0
          ? besoin.keywords[0].toLowerCase()
          : "";
      case "pride":
        return besoin.pride || 0;
      case "frequency": {
        const days = PERIOD_DAYS[besoin.frequency?.period] || 1;
        return (besoin.frequency?.count || 0) / days;
      }
      case "status":
        return besoin.status || 0;
      default:
        return 0;
    }
  };

  // --- Applique le tri par colonne si une colonne est sélectionnée ---
  const displayedBesoins = useMemo(() => {
    if (!sortColumn) return filteredBesoins;

    const sorted = [...filteredBesoins].sort((a, b) => {
      const va = getSortValue(a, sortColumn);
      const vb = getSortValue(b, sortColumn);
      if (va < vb) return sortDirection === "asc" ? -1 : 1;
      if (va > vb) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBesoins, sortColumn, sortDirection]);

  const handleColumnSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
  };


  // --- Ajouter ou modifier un besoin ---
  const handleSaveBesoin = async (besoin) => {
    try {
      if (besoinToEdit) {
        await updateBesoin({ ...besoinToEdit, ...besoin });
      } else {
        await addBesoin(besoin);
      }
      await refreshBesoins();

      setIsQuickBesoinOpen(false);
      setBesoinToEdit(null);
    } catch (error) {
      console.error("Erreur de sauvegarde :", error);
      alert(error.message || "Impossible de sauvegarder le besoin. Veuillez réessayer.");
    }
  };

  // --- Supprimer un besoin ---
  const handleDeleteBesoin = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/besoins/${id}`, { method: "DELETE" });

      if (!res.ok) {
        throw new Error("Erreur lors de la suppression du besoin");
      }

      deleteBesoin(id);
      await refreshBesoins();
    } catch (error) {
      console.error("Erreur de suppression :", error);
      alert("Impossible de supprimer le besoin. Veuillez réessayer.");
    }
  };

  return (
    <div className="besoin-container">
      {/* --- Barre d'action --- */}
      <div
        className="besoin-toolbar"
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          padding: "0.5rem 0",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          background: "#2b2f38",
          borderRadius: "12px",
          marginBottom: "0.8rem",
          gap: "0.8rem",
        }}
      >
        {/* Recherche */}
        <Button variant="link" onClick={() => setActivePanel(activePanel === "search" ? null : "search")}>
          <Search size={22} color={activePanel === "search" || searchQuery ? "#6F3F89" : "#bbb"} />
        </Button>

        {/* Filtres de statut */}
        <Button variant="link" onClick={() => setActivePanel(activePanel === "filter" ? null : "filter")}>
          <Funnel size={22} color={activePanel === "filter" || activeStatusFilters.length > 0 ? "#6F3F89" : "#bbb"} />
        </Button>

        {/* Tri chronologique */}
        <Button variant="link" onClick={() => setReverseOrder((prev) => !prev)}>
          {reverseOrder ? <SortAlphaUp size={22} color="#6F3F89" /> : <SortAlphaDown size={22} color="#bbb" />}
        </Button>

        {/* Reset général */}
        <Button
          variant="link"
          onClick={() => {
            setSearchQuery("");
            setActiveStatusFilters([]);
            setReverseOrder(false);
            setActivePanel(null);
            setSortColumn(null);
            setSortDirection("asc");
          }}
        >
          <XCircle
            size={22}
            color={searchQuery || activeStatusFilters.length > 0 ? "#6F3F89" : "#bbb"}
          />
        </Button>

        {/* Ajout rapide */}
        <Button
          variant="link"
          className="quick-besoin-add-btn"
          onClick={() => {
            setBesoinToEdit(null);
            setIsQuickBesoinOpen(true);
          }}
        >
          <PencilSquare size={30} color="#A3A3A3" />
        </Button>
      </div>

      {/* --- Panneaux dynamiques --- */}
      <div className="besoin-active-panel" style={{ marginBottom: "1rem" }}>
        {activePanel === "search" && (
          <div
            className="besoin-search-bar"
            style={{
              display: "flex",
              alignItems: "center",
              margin: "auto",
              marginBottom: "1rem",
              background: "#2b2f38",
              borderRadius: "12px",
              padding: "0.5rem 0.8rem",
              minWidth: "335px",
              maxWidth: "50%",
            }}
          >
            <Form.Control
              ref={searchInputRef}
              type="text"
              placeholder={t('besoin.filters.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                background: "#f5f5f5",
                border: "none",
                color: "#333",
                borderRadius: "8px",
                padding: "0.4rem 2rem 0.4rem 0.6rem",
                margin: "auto",
              }}
            />
            {searchQuery && (
              <Button
                variant="link"
                onClick={() => setSearchQuery("")}
                style={{ marginLeft: "-2rem", zIndex: 2, color: "#6F3F89", padding: 0 }}
                title="Effacer"
              >
                <XCircle size={20} />
              </Button>
            )}
          </div>
        )}

        {activePanel === "filter" && (
          <div
            className="besoin-filter-panel"
            style={{
              background: "#2b2f38",
              borderRadius: "12px",
              padding: "0.5rem",
              display: "flex",
              justifyContent: "space-around",
              flexWrap: "wrap",
              maxWidth: "550px",
              gap: "0.8rem",
              margin: "auto",
            }}
          >
            {STATUS_IDS.map((id) => {
              const isActive = activeStatusFilters.includes(id);
              const count = besoinsCountByStatus[id] || 0;
              return (
                <div
                  key={id}
                  onClick={() =>
                    setActiveStatusFilters((prev) =>
                      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
                    )
                  }
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    gap: "0.2rem",
                    padding: "0.3rem 0.6rem",
                    borderRadius: "8px",
                    border: isActive ? "1px solid #565556" : "1px solid transparent",
                  }}
                >
                  <span style={{ color: "#ddd", fontSize: "0.85rem", fontWeight: "500" }}>
                    {t(`besoin.status_${id}`)}
                  </span>
                  <span style={{ color: "#aaa", fontSize: "1rem" }}>{count > 0 ? count : ""}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* --- Modale d'ajout / modification --- */}
      <QuickBesoinModal
        show={isQuickBesoinOpen}
        onClose={() => {
          setIsQuickBesoinOpen(false);
          setBesoinToEdit(null);
        }}
        onSave={handleSaveBesoin}
        scanResultId={null}
        existingBesoin={besoinToEdit}
      />

      {/* --- Liste des besoins --- */}
      {loading ? (
        <p className="loading-text">{t('besoin.loading')}</p>
      ) : (
        <div className="besoins-list">
          {/* En-tête de colonnes, cliquable pour trier — alignée sur la même grille que les lignes */}
          {displayedBesoins.length > 0 && (
            <div className="besoin-row-grid besoin-row-header">
              {COLUMNS.map((col) => (
                <div
                  key={col.key}
                  className={`besoin-col besoin-col-header besoin-col-${col.key}`}
                  onClick={() => handleColumnSort(col.key)}
                >
                  {t(col.label)}
                  {sortColumn === col.key && (
                    sortDirection === "asc" ? <CaretUpFill size={10} /> : <CaretDownFill size={10} />
                  )}
                </div>
              ))}
              <div className="besoin-col besoin-col-header besoin-col-chevron" />
            </div>
          )}

          {displayedBesoins.map((besoin) => (
            <BesoinCard
              key={besoin._id}
              besoin={besoin}
              highlightQuery={searchQuery}
              onEdit={(b) => {
                setBesoinToEdit(b);
                setIsQuickBesoinOpen(true);
              }}
              onDelete={handleDeleteBesoin}
            />
          ))}
          {displayedBesoins.length === 0 && (
            <p className="loading-text">{t('besoin.empty')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Besoin;